import { ActivePlaylist, Playlist } from "../../../config/audio/types";
import { LOGS } from "../logging";
import { AudioEngine, WebAudioApiEngine } from "./audioEngine";
import { diffPlaylists } from "./audioCalculations";
import { eventAppError } from "../gtag";

export interface AudioEngineState {
	activePlaylists: Map<string, ActivePlaylist>;
}
/**
 * Audio Player - Public interface for audio operations
 * This is the main API that external code should use
 */
export class AudioPlayer {
	private engine: AudioEngine;
	private updateInProgress = false;
	private pendingUpdates: readonly Playlist[][] = [];

	constructor() {
		this.engine = new WebAudioApiEngine();
	}

	/**
	 * Initialize the audio player - must be called after user gesture
	 */
	async initialize(): Promise<void> {
		try {
			await this.engine.init();
			this.updateInProgress = false;
			this.pendingUpdates = [];
		} catch (error) {
			console.error("AudioPlayer: Initialization failed:", error);
			eventAppError(`Failed to initialized audio player: ${error}`, "audio");
			throw error;
		}
	}

	/**
	 * Check if the player is initialized
	 */
	isInitialized(): boolean {
		return this.engine.initialized;
	}

	/**
	 * Play a one-shot sound effect
	 */
	async playOneShot(playlist: Playlist): Promise<void> {
		if (!this.isInitialized()) {
			LOGS.AUDIO && console.log("Audio player not initialized, skipping one-shot");
			return;
		}
		await this.engine.playOneShot(playlist);
	}

	/**
	 * Update playlists with smart queuing to handle concurrent updates
	 */
	async updatePlaylists(next: readonly Playlist[]): Promise<void> {
		if (!this.isInitialized()) {
			LOGS.AUDIO && console.log("Audio player not initialized, skipping update");
			return;
		}

		if (this.updateInProgress) {
			this.pendingUpdates = [...this.pendingUpdates, [...next]];
			LOGS.AUDIO &&
				console.log(
					`Update already in progress, pending update with ${next.length} playlists`,
				);
			return;
		}

		this.updateInProgress = true;

		try {
			await this.processUpdate(next);

			while (this.hasPendingUpdate()) {
				const [...pendingPlaylists] = this.pendingUpdates;
				const nextupdate = pendingPlaylists.shift();
				if (!nextupdate) {
					return;
				}
				LOGS.AUDIO &&
					console.log(
						`Processing pending update with ${pendingPlaylists.length} playlists`,
					);
				await this.processUpdate(nextupdate);
				this.pendingUpdates = [...pendingPlaylists];
			}
		} finally {
			this.updateInProgress = false;
		}
	}

	/**
	 * Internal method to process a single update
	 */
	private async processUpdate(playlists: readonly Playlist[]): Promise<void> {
		try {
			if (!this.engine.initialized) {
				return;
			}

			const currentPlaylists = this.engine.activePlaylists;

			const { toStop, toStart, toUpdate } = diffPlaylists(currentPlaylists, playlists);

			// Stop playlists that are no longer needed
			toStop.forEach((playlist) => this.engine.stopPlaylist(playlist.name));

			// Update existing playlists
			toUpdate.forEach((nextPlaylist) => {
				const currentPlaylist = this.engine.getPlaylist(nextPlaylist.name);
				if (currentPlaylist) {
					this.adjustPlaylist(currentPlaylist, nextPlaylist);
				}
			});

			// Start new playlists
			for (const playlist of toStart) {
				try {
					await this.engine.startPlaylist(playlist);
				} catch (err) {
					eventAppError(`Failed start playlist ${playlist.name}: ${err}`, "audio");
					console.error("Failed to start playlist:", playlist.name, err);
				}
			}
		} catch (error) {
			console.error("Error processing playlist update:", error);
			eventAppError(`Error processing playlist update: ${error}`, "audio");
		}
	}

	/**
	 * Adjust existing playlist (internal method)
	 */
	private adjustPlaylist(current: ActivePlaylist, next: Playlist): void {
		this.engine.updatePlaylist(current, next);
	}

	/**
	 * Get debug information about the player state
	 */
	getDebugInfo() {
		const state = this.engine.state;
		const playlistNames = Array.from(state.activePlaylists.keys());

		return {
			isInitialized: this.isInitialized(),
			updateInProgress: this.updateInProgress,
			hasPendingUpdate: this.hasPendingUpdate(),
			pendingUpdatePlaylistCount: this.pendingUpdates.length,
			audioContextState: this.engine.status,
			activePlaylistCount: state.activePlaylists.size,
			activePlaylistNames: playlistNames,
		};
	}

	private hasPendingUpdate(): boolean {
		return this.pendingUpdates.length > 0;
	}

	/**
	 * Cleanup all resources
	 */
	async destroy(): Promise<void> {
		try {
			await this.engine.destroy();
		} catch (error) {
			console.error("Error destroying audio engine:", error);
			eventAppError(`Error destroying audio engine: ${error}`, "audio");
		}
	}
}

export const audioPlayer = new AudioPlayer();
